import React from 'react';
import {
    Create,
    Edit,
    SimpleForm,
    usePermissions,
    useRecordContext,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {Box} from '@mui/material';
import {DeleteOutline} from '@mui/icons-material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useUmbrellaChoices} from '../lib/customHooks';
import hasPermission from '../auth/hasPermission';

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const HeaderBar = ({mode, record, translate, canDelete, onDelete}) => {
    const title = mode === 'create'
        ? translate('Create export settings')
        : record?.parent_charity_name || translate('Export settings');
    const statusVariant = record?.export_active ? 'success' : 'neutral';

    return (
        <PageHeader
            breadcrumb={{label: translate('Export Settings'), to: '/exportSettings'}}
            title={
                mode === 'create' ? (
                    <Box component="span">{title}</Box>
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant}/>
                        <Box component="span">{title}</Box>
                    </Box>
                )
            }
            actions={
                <>
                    <SaveAction label={mode === 'create' ? translate('Create export settings') : translate('Save changes')}/>
                    {mode === 'edit' && canDelete && (
                        <PrimaryButton
                            variant="danger"
                            startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                            onClick={onDelete}
                        >
                            {translate('Delete')}
                        </PrimaryButton>
                    )}
                </>
            }
        />
    );
};

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const umbrellaChoices = useUmbrellaChoices();
    const deleteConfirm = useDeleteConfirm();

    const canSeeUmbrella = !!hasPermission(permissions, ['view'], 'umbrella');
    const canDelete = permissions === 'admin';

    if (!umbrellaChoices) return null;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <HeaderBar
                mode={mode}
                record={record}
                translate={translate}
                canDelete={canDelete}
                onDelete={deleteConfirm.openDialog}
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="exportSettings"
                    record={record}
                    recordLabel={record.parent_charity_name}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 320px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <SectionCard title={translate('Integration')} subtitle="OVAS API endpoint and credentials">
                        {canSeeUmbrella && (
                            <Box sx={{mb: 2}}>
                                <StatamicSelectInput
                                    source="parent_charity_id"
                                    label={translate('Umbrella')}
                                    required
                                    choices={umbrellaChoices}
                                />
                            </Box>
                        )}
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <StatamicTextInput
                                source="api_endpoint"
                                label={translate('Api endpoint')}
                                type="url"
                                required
                            />
                            <StatamicTextInput
                                source="api_key"
                                label={translate('Api key')}
                                required
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Accounting identifiers')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput
                                source="ledger_id"
                                label={translate('Ledger ID')}
                                required
                            />
                            <StatamicTextInput
                                source="contract_id"
                                label={translate('Contract ID')}
                                required
                            />
                        </Box>
                        <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
                            <StatamicTextInput
                                source="category_id_sponsor_default"
                                label={translate('Category ID sponsor default')}
                                required
                            />
                            <StatamicTextInput
                                source="category_id_sponsor_foreign"
                                label={translate('Category ID sponsor foreign')}
                                required
                            />
                            <StatamicTextInput
                                source="category_id_sponsor_cancelled"
                                label={translate('Category ID sponsor cancelled')}
                                required
                            />
                        </Box>
                    </SectionCard>

                    <SectionCard title={translate('Import credentials')}>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                            <StatamicTextInput
                                source="import_user_name"
                                label={translate('Import username')}
                            />
                            <StatamicTextInput
                                source="import_password"
                                label={translate('Import password')}
                                type="password"
                            />
                        </Box>
                    </SectionCard>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    <SectionCard title={translate('Status')}>
                        <ToggleRow
                            source="export_active"
                            label={translate('Is active')}
                            subtitle="Disable to pause the OVAS export for this umbrella"
                            defaultValue={true}
                        />
                    </SectionCard>
                </Box>
            </Box>
        </Box>
    );
};

export const ExportSettingsEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const ExportSettingsCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="edit">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default ExportSettingsEditStatamic;
